import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * Sign-in gate modal for Sitecore Forms File Upload fields. When an
 * unauthenticated visitor tries to attach a file, initFileUploadAuthGate blocks
 * the native file picker and dispatches `thread:form-auth-gate:open` with the
 * site-correct login URL. This modal then offers:
 *   - Cancel  -> closes the modal, nothing else happens.
 *   - Sign In -> POSTs to the login URL (same mechanism as the global header /
 *                gated-content CTA), navigating the user to the sign-in flow.
 */
const FileUploadAuthGateModal = () => {
  const [open, setOpen] = useState(false);
  const [loginUrl, setLoginUrl] = useState("");
  const dialogRef = useRef(null);
  const openRef = useRef(false);

  const close = useCallback(() => {
    if (!openRef.current) return;
    openRef.current = false;
    setOpen(false);
    setLoginUrl("");
    document.documentElement.style.overflow = "";
    document.body.style.overflow = "";
  }, []);

  const signIn = useCallback(() => {
    // Login is initiated with a POST to the resolved sign-in URL, mirroring the
    // global header and gated-content login forms.
    if (loginUrl) {
      const form = document.createElement("form");
      form.method = "post";
      form.action = loginUrl;
      document.body.appendChild(form);
      form.submit();
      return;
    }
    // Fail safe: if the URL could not be resolved server-side, go to the root.
    window.location.href = "/";
  }, [loginUrl]);

  useEffect(() => {
    const onOpen = (e) => {
      setLoginUrl((e.detail && e.detail.loginUrl) || "");
      openRef.current = true;
      setOpen(true);
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
    };

    const onKey = (e) => {
      if (!openRef.current) return;
      if (e.key === "Escape" || e.keyCode === 27) {
        e.preventDefault();
        close();
      }
    };

    window.addEventListener("thread:form-auth-gate:open", onOpen);
    document.addEventListener("keydown", onKey, true);

    return () => {
      window.removeEventListener("thread:form-auth-gate:open", onOpen);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [close]);

  useEffect(() => {
    if (open && dialogRef.current) {
      const firstBtn = dialogRef.current.querySelector("button");
      if (firstBtn) firstBtn.focus();
    }
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="file-upload-auth-gate"
      role="dialog"
      aria-modal="true"
      aria-labelledby="file-upload-auth-gate-title"
      aria-describedby="file-upload-auth-gate-detail"
      onMouseDown={(e) => {
        // Clicking the backdrop (outside the dialog) cancels.
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="file-upload-auth-gate__dialog" ref={dialogRef}>
        <div className="file-upload-auth-gate__icon" aria-hidden="true">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>
        <h2
          id="file-upload-auth-gate-title"
          className="file-upload-auth-gate__title"
        >
          Sign in to upload files
        </h2>
        <p
          id="file-upload-auth-gate-detail"
          className="file-upload-auth-gate__detail"
        >
          You need to be signed in to attach and upload files. Please sign in to
          continue, or cancel to go back.
        </p>
        <div className="file-upload-auth-gate__actions">
          <button
            type="button"
            className="btn file-upload-auth-gate__btn file-upload-auth-gate__btn--cancel"
            onClick={close}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary file-upload-auth-gate__btn"
            onClick={signIn}
          >
            Sign In
          </button>
        </div>
      </div>
    </div>
  );
};

export default FileUploadAuthGateModal;
